import type { ReplayMetricKey } from "../model/types";
import type { ReplayTeamMetrics } from "../api/use-team-metrics";

/**
 * KAN-794/796: ярлык стиля команды вместо голых цифр.
 *
 * Само по себе «приоритет объектов 0.72» не значит ничего: 0.72 — это много или
 * мало? Ответ существует только в сравнении с остальными командами турнира.
 * Поэтому стиль — это ярлык, выданный по отклонению от медианы, а числа остаются
 * рядом как обоснование, а не как единственное содержание витрины.
 *
 * Вынесено из компонента в отдельный модуль ради тестов: медиана — ровно тот
 * расчёт, который ломается тихо. Ошибка в нём не роняет страницу, а выдаёт
 * правдоподобный ярлык не той команде.
 */

/** Метрики, по которым выдаётся ярлык, и как называть отклонение в каждую сторону. */
// KAN-901: ключ типизирован ReplayMetricKey, а не string — опечатка в имени оси
// иначе означала бы молча пропавший ярлык, а не ошибку сборки.
export const STYLE_RULES: Array<{ key: ReplayMetricKey; high: string; low?: string }> = [
  { key: "objectiveFocus", high: "map", low: "fights" },
  { key: "shotcalling", high: "coordinated" },
  { key: "towerDiveAggression", high: "diving" },
  { key: "ccPressure", high: "control" },
  { key: "visionQuality", high: "vision" },
];

/** Оси, которые показываются числами под ярлыком. */
export const STYLE_AXES = STYLE_RULES.map((r) => r.key);

/**
 * Насколько метрика должна отличаться от медианы, чтобы получить ярлык.
 *
 * 15% выбраны так, чтобы ярлык получала не каждая команда: если «через карту»
 * написано у всех восьми, слово перестаёт что-либо значить.
 */
export const STYLE_THRESHOLD = 0.15;

/** Больше двух ярлыков описывают уже не стиль, а всё сразу. */
export const MAX_STYLE_TAGS = 2;

export function metricValue(team: ReplayTeamMetrics, key: ReplayMetricKey): number | null {
  const metric = team.metrics.find((m) => m.key === key);
  return metric?.value ?? null;
}

export function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1]! + sorted[mid]!) / 2 : sorted[mid]!;
}

/**
 * База сравнения — только сопоставленные команды.
 *
 * Несопоставленная строка это ОДНА сторона ОДНОЙ игры, а не команда за турнир:
 * другая единица наблюдения. Подмешивать её в медиану значит сдвигать порог, по
 * которому выдаются ярлыки, — и на турнире с несколькими такими строками ярлык
 * доставался не той команде или не доставался вовсе.
 *
 * Общее правило, которое здесь и нарушалось: прежде чем считать долю, медиану
 * или порог, ответить, из каких наблюдений собран знаменатель и одной ли они
 * природы.
 */
export function comparableTeams(rows: ReplayTeamMetrics[]): ReplayTeamMetrics[] {
  return rows.filter((row) => row.teamId !== null);
}

/**
 * KAN-770: меньше трёх команд — ярлыки не выдаются вовсе.
 *
 * На двух командах медиана это их среднее, и каждая гарантированно отклоняется от
 * неё на одну и ту же величину в разные стороны: ярлыки получат обе, механически.
 * Причём описывать они будут не стиль команды, а разницу между двумя — поданную
 * как свойство. На одной команде медиана равна её собственному значению, и ярлыков
 * нет уже арифметически; вредны именно два.
 *
 * Порог здесь не про точность, а про смысл утверждения: «Игра через карту» означает
 * «заметно чаще остальных», и слово «остальные» требует, чтобы их было больше одного.
 */
export const MIN_COMPARABLE_TEAMS = 3;

/**
 * Ярлыки стиля команды. `all` — база сравнения; передавать сюда результат
 * {@link comparableTeams}, а не сырой ответ API.
 *
 * Пустой массив возвращается в двух разных случаях: команда близка к медиане, и
 * сравнивать не с кем ({@link MIN_COMPARABLE_TEAMS}). Витрине этого различать не
 * нужно — она в обоих случаях не рисует ярлыков, — но подпись «мало данных» при
 * желании строится по длине базы сравнения, а не по пустому результату.
 */
export function styleTagsFor(team: ReplayTeamMetrics, all: ReplayTeamMetrics[]): string[] {
  if (all.length < MIN_COMPARABLE_TEAMS) return [];

  const tags: Array<{ tag: string; deviation: number }> = [];

  for (const rule of STYLE_RULES) {
    const value = metricValue(team, rule.key);
    const base = median(
      all.map((t) => metricValue(t, rule.key)).filter((v): v is number => v !== null),
    );
    // Нулевая медиана делит на ноль, отсутствующая метрика сравнивать не с чем.
    if (value === null || base === null || base === 0) continue;

    const deviation = (value - base) / base;
    if (deviation >= STYLE_THRESHOLD) {
      tags.push({ tag: rule.high, deviation });
    } else if (rule.low && deviation <= -STYLE_THRESHOLD) {
      tags.push({ tag: rule.low, deviation: -deviation });
    }
  }

  return tags
    .sort((a, b) => b.deviation - a.deviation)
    .slice(0, MAX_STYLE_TAGS)
    .map((t) => t.tag);
}
